import React, { useState } from 'react';
import { Popover, Skeleton } from 'antd';
import { getReturnInspectionRule } from '../../services/returnInspection';
import { DecisionFlow, ReturnInspectionRule } from '../../types/returnInspection';
import { contentStageReachable, describeFlow } from './decisionFlow';
import { MODE_OUTCOME } from './ruleDisplay';

/**
 * 规则内容只会随"发布新版本"变化（新版本是新的一行、新的 id），所以同一个 id 的内容永远不变，
 * 取回来之后可以一直缓存，鼠标反复悬停不会重复请求。
 */
const ruleCache = new Map<number, ReturnInspectionRule>();

export const previewLines = (rule: ReturnInspectionRule): string[] => {
  if (rule.mode !== 'conditional') return [MODE_OUTCOME[rule.mode]];
  const flow = rule.decision_flow as DecisionFlow;
  if (!flow?.appearance_ok) return [MODE_OUTCOME.conditional];
  const lines = ['按流程初检', ...describeFlow(flow, !!rule.packaging_sealed)];
  const baseline = [`外观参考照片 ${rule.packaging_baseline_photos.length} 张`];
  if (contentStageReachable(flow)) {
    const named = rule.content_checklist.filter((row) => row.item.trim());
    const pieces = named.reduce((sum, row) => sum + (Number(row.qty) || 0), 0);
    baseline.push(
      `内件物品 ${named.length} 种 ${pieces} 件`,
      `内件参考照片 ${rule.content_baseline_photos.length} 张`,
    );
  }
  lines.push(baseline.join(' · '));
  return lines;
};

/** 鼠标悬停时，显示这条库存单元自设规则的简要内容（处理方式 / 检查流程 / 基准资料概况） */
const UnitRulePreview: React.FC<{ ruleId: number; children: React.ReactNode }> = ({
  ruleId,
  children,
}) => {
  const [rule, setRule] = useState<ReturnInspectionRule | null>(ruleCache.get(ruleId) ?? null);
  const [failed, setFailed] = useState(false);

  const handleOpenChange = async (open: boolean) => {
    if (!open || ruleCache.has(ruleId)) {
      if (open && !rule) setRule(ruleCache.get(ruleId) ?? null);
      return;
    }
    try {
      const loaded = await getReturnInspectionRule(ruleId);
      ruleCache.set(ruleId, loaded);
      setRule(loaded);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  };

  const content = rule ? (
    <div style={{ maxWidth: 420, lineHeight: '22px' }}>
      {previewLines(rule).map((line, index) => (
        // 不能用文字当 key：不同行的文字可能相同
        // eslint-disable-next-line react/no-array-index-key
        <div key={index}>{line}</div>
      ))}
    </div>
  ) : failed ? (
    <span style={{ color: '#999' }}>规则内容加载失败</span>
  ) : (
    <Skeleton active paragraph={{ rows: 2 }} title={false} style={{ width: 240 }} />
  );

  return (
    <Popover
      placement="topLeft"
      mouseEnterDelay={0.2}
      title="库存单元自设规则"
      content={content}
      onOpenChange={handleOpenChange}
    >
      {children}
    </Popover>
  );
};

export default UnitRulePreview;
